import PropTypes from 'prop-types';
import React, {
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
// ROUTES
import { useParams } from 'react-router-dom';
// TRANSLATION
import { useTranslation } from 'react-i18next';
// CONFIG
import navigationIcons from '../../../config/navigationIcons';
// HOOKS
import useUpdateEffect from '../../../Hooks/useUpdateEffect';
// CONTEXTS
import ApplicationContext from '../../Context/ApplicationContext';
import { AuthContext } from '../../Context/AuthContext';
import { MessageContext } from '../../Context/MessageContext';
// HELPERS
import {
    createNewEntityFromChildren,
    recursiveMap,
} from '../../../Helpers/recursions';
// COMPONENTS
import Button from '../Buttons/Button';
import Modal from '../Modal';
import Submit from './Submit';

// STYLES
import style from '../../../../scss/components/form.module.scss';

const computeChangedFields = (current, initial) => {
    if (!current || !initial) {
        return [];
    }

    const keys = new Set([...Object.keys(current), ...Object.keys(initial)]);

    const changed = [];
    keys.forEach((key) => {
        if (JSON.stringify(current[key]) !== JSON.stringify(initial[key])) {
            changed.push(key);
        }
    });

    return changed;
};

const getResourceName = (resource) =>
    typeof resource === 'string' ? resource : resource?.name;

const getDraftStorageKey = (resourceName, id) => {
    if (resourceName !== 'articles') {
        return null;
    }

    return id
        ? `form-draft-${resourceName}-${id}`
        : `form-draft-${resourceName}-new`;
};

const readDraftFromSession = (key, pushMessage) => {
    if (!key) {
        return null;
    }

    try {
        const raw = sessionStorage.getItem(key);

        if (!raw) {
            return null;
        }

        return JSON.parse(raw);
    } catch (error) {
        pushMessage('Failed to read draft from sessionStorage.', error);
        return null;
    }
};

const writeDraftToSession = (key, entity, pushMessage) => {
    if (!key) return;

    try {
        sessionStorage.setItem(key, JSON.stringify(entity));
    } catch (error) {
        pushMessage('Failed to write draft to sessionStorage.', error);
    }
};

const clearDraftFromSession = (key, pushMessage) => {
    if (!key) return;

    try {
        sessionStorage.removeItem(key);
    } catch (error) {
        pushMessage('Failed to clear draft from sessionStorage.', error);
    }
};

const cloneEntity = (value, pushMessage) => {
    if (value == null) {
        return value;
    }

    try {
        return JSON.parse(JSON.stringify(value));
    } catch (error) {
        pushMessage('Failed to clone entity.', error);
        return value;
    }
};

/**
 * This component is used for general Write/Edit operations
 * @param service The service layer used for communicating with the backend.
 * @param resource The current resource being edited (eg.: Recipe, Article, etc...).
 * @param history The history
 * @param immutable If the form is used for displaying data only. Will refuse to forward changes to the backend.
 * @param children The children that will receive the required props from the component
 * @param creates If the form creates another, related resource it will redirect to this resource on successful save
 * @returns {null|*}
 * @constructor
 */
const Form = ({
    service,
    resource,
    history,
    children,
    immutable = false,
    creates = null,
}) => {
    const setPageInfo = useContext(ApplicationContext);
    const { user } = useContext(AuthContext);
    const { t } = useTranslation();
    const { id } = useParams();
    const { pushMessage } = useContext(MessageContext);

    const formRef = useRef(null);
    const initialEntityRef = useRef(null);
    const skipDraftSyncRef = useRef(false);

    const resourceName = getResourceName(resource);
    const draftStorageKey = getDraftStorageKey(resourceName, id);

    const [state, setState] = useState({
        entity: {},
        errors: {},
        display: false,
        loading: true,
        changed: false,
        created: false,
        lock: null,
        hasDraft: false,
    });

    const { entity, errors, loading, hasDraft } = state;

    const update = (change) => {
        setState((oldState) => ({ ...oldState, ...change }));
    };

    // MODEL LOCK CALLBACKS
    const passLock = () =>
        service.passLock(state.lock, user).then(() => history.go(0));

    const back = () => history.goBack();

    const scrollToFirstError = () => {
        if (!formRef.current) {
            return;
        }

        const error = formRef.current.querySelector(
            `.${style.isInvalid}`,
        )?.parentNode;

        if (error) {
            error.scrollIntoView();
            window.scrollBy(0, -70);
        }
    };

    const discardDraft = () => {
        if (!draftStorageKey) {
            return;
        }

        skipDraftSyncRef.current = true;
        clearDraftFromSession(draftStorageKey, pushMessage);

        update({
            entity: cloneEntity(initialEntityRef.current, pushMessage) || {},
            errors: {},
            changed: false,
            hasDraft: false,
        });

        pushMessage({
            title: 'Draft successfully discarded!',
        });
    };

    const save = () => {
        if (immutable || !state.changed || loading) {
            return;
        }

        update({
            loading: true,
            changed: false,
            errors: {},
        });

        service
            .store(resource, entity, entity.id)
            .then((result) => {
                const savedEntity = { ...entity, ...result.data };

                skipDraftSyncRef.current = true;
                clearDraftFromSession(draftStorageKey, pushMessage);
                initialEntityRef.current = cloneEntity(
                    savedEntity,
                    pushMessage,
                );

                if (creates) {
                    history.push(`/${creates}/${result.id}/show`);
                    return;
                }

                update({
                    entity: savedEntity,
                    created: !entity.id,
                    loading: false,
                    changed: false,
                    hasDraft: false,
                });

                pushMessage({ title: 'Saved successfully!' });
            })
            .catch(({ response }) => {
                skipDraftSyncRef.current = false;

                const responseErrors = response?.data?.errors || {};
                const firstError = Object.values(responseErrors)?.[0]?.[0];
                const fallbackMessage =
                    response?.data?.message || 'Editing failed!';

                pushMessage({
                    title: firstError || fallbackMessage,
                    type: 'error',
                });

                update({
                    errors: responseErrors,
                    changed: true,
                    loading: false,
                });
                scrollToFirstError();
            });
    };

    // Callbacks
    const onSave = (e) => {
        e.stopPropagation();
        e.preventDefault();
        save();
    };

    const onChange = useCallback((change) => {
        if (
            !change ||
            typeof change !== 'object' ||
            change.nativeEvent ||
            change.target
        ) {
            return;
        }

        skipDraftSyncRef.current = false;

        setState(({ entity: currentEntity, ...rest }) => ({
            ...rest,
            entity: { ...currentEntity, ...change },
            changed: true,
            hasDraft: true,
        }));
    }, []);

    const onToggle = onChange;

    /**
     * Runs on component mount, either loads specified entity or creates a new one, hooks into page lifecycle
     */
    useEffect(() => {
        setPageInfo({
            title: t(`application.edit`, {
                resource: t(`${resourceName}.resource`),
            }),
            icon: navigationIcons[resourceName],
        });

        if (id) {
            service
                .find(resource, id)
                .then((data) => {
                    const baseEntity = data.data;
                    const persistedDraft =
                        readDraftFromSession(draftStorageKey);

                    initialEntityRef.current = cloneEntity(
                        baseEntity,
                        pushMessage,
                    );

                    update({
                        entity: persistedDraft
                            ? { ...baseEntity, ...persistedDraft }
                            : baseEntity,
                        loading: false,
                        display: true,
                        changed: !!persistedDraft,
                        hasDraft: !!persistedDraft,
                    });
                })
                .catch(({ response }) => {
                    if (!response) {
                        return;
                    }

                    if (response.status === 423) {
                        update({
                            lock: response.data,
                            display: true,
                            loading: false,
                        });
                    }
                });

            return;
        }

        const initialEntity = createNewEntityFromChildren(children);
        const persistedDraft = readDraftFromSession(
            draftStorageKey,
            pushMessage,
        );

        initialEntityRef.current = cloneEntity(initialEntity, pushMessage);

        update({
            loading: false,
            display: true,
            entity: persistedDraft
                ? { ...initialEntity, ...persistedDraft }
                : initialEntity,
            changed: !!persistedDraft,
            hasDraft: !!persistedDraft,
        });
    }, []);

    useEffect(() => {
        if (
            !draftStorageKey ||
            loading ||
            !state.display ||
            !state.changed ||
            skipDraftSyncRef.current
        ) {
            return;
        }

        writeDraftToSession(draftStorageKey, entity, pushMessage);
    }, [draftStorageKey, entity, loading, state.display, state.changed]);

    /**
     * Resets state when a new entity has been created
     */
    useUpdateEffect(() => {
        update({
            display: true,
            loading: false,
            created: false,
            changed: false,
        });
    }, [id]);

    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            scrollToFirstError();
        }
    }, [errors]);

    useEffect(() => {
        window.onbeforeunload = state.changed ? () => true : null;

        return () => {
            window.onbeforeunload = null;
        };
    }, [state.changed]);

    /**
     * Check periodically for a lock status in case the entity lock was taken away by some other user
     */
    useEffect(() => {
        const interval = setInterval(() => {
            if (state.entity && state.entity.lock) {
                service.lockStatus(state.entity.lock).catch(({ response }) => {
                    update({
                        lock: response.status === 423 ? response.data : null,
                    });
                });
            }
        }, 5000);

        return () => clearInterval(interval);
    }, [state.entity]);

    /**
     * If component is not ready to display its content yet return null
     */
    if (!state.display) {
        return null;
    }

    /**
     * If a new entity has been created rewrite url and flag as existing
     */
    if (state.created && history) {
        history.push(`/${resourceName}/${state.entity.id}/show`);
        update({ created: false });
    }

    const changedFields = computeChangedFields(
        entity,
        initialEntityRef.current,
    );

    const childrenWithProps = recursiveMap(children, (child) => {
        if (typeof child.type === 'string') {
            return child;
        }

        return React.cloneElement(child, {
            service,
            resource,
            entity,
            changedFields,
            errors,
            onChange,
            onToggle,
            hasDraft,
            onDiscardDraft: discardDraft,
        });
    });

    return (
        <>
            {state.lock && (
                <Modal isOpen={!!state.lock} onRequestClose={back}>
                    <h2>
                        {t(`${resourceName}.resource`)} zárolva
                        <strong>{state.lock.user.username}</strong> által
                    </h2>
                    <div>
                        <Button name="passLock" click={passLock} />
                        <Button name="back" click={back} />
                    </div>
                </Modal>
            )}

            {!state.lock && (
                <form onSubmit={onSave} ref={formRef}>
                    <div>{childrenWithProps}</div>
                    {!immutable && <Submit />}
                </form>
            )}
        </>
    );
};

export default Form;

Form.propTypes = {
    /**
     * Type of service
     */
    service: PropTypes.object.isRequired,
    /**
     * Type of resource
     */
    resource: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            name: PropTypes.string,
        }),
    ]).isRequired,
    /**
     * Type of history
     */
    history: PropTypes.object.isRequired,
    /**
     * Type of immutable
     */
    immutable: PropTypes.bool,
    /**
     * Type of children
     */
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]).isRequired,
    /**
     * Type of creates
     */
    creates: PropTypes.string,
};

Form.defaultProps = {
    immutable: false,
    creates: null,
};
